import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Calendar, Clock, User, MapPin, Edit, Trash2, Repeat, CheckCircle, Camera, ClipboardList, Image, Loader2 } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import DateInput from '../components/DateInput';
import RecurrenceModal from '../components/RecurrenceModal';
import Autocomplete from '../components/Autocomplete';
import CompleteServiceModal from '../components/CompleteServiceModal';
import { RatingForm } from '../components/RatingForm';
import QuickCreateClientModal from '../components/QuickCreateClientModal';
import QuickCreatePropertyModal from '../components/QuickCreatePropertyModal';
import QuickCreateEmployeeModal from '../components/QuickCreateEmployeeModal';
import StatusBadge from '../components/StatusBadge';
import { useIsMobile } from '../hooks/useMediaQuery';
import Pagination from '../components/Pagination';
import { useKeyboardShortcuts, type KeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import '../components/Recurrence.css';
import '../styles/responsive.css';
import MiniMonthCalendar, { type MiniCalendarEventDay } from '../components/MiniMonthCalendar';
import { searchClients } from '../services/clientApi';
import { searchProperties, getPropertiesByClient } from '../services/propertyApi';
import type { Property } from '../services/propertyApi';
import { getAppointments, updateAppointment as updateAppointmentAPI, deleteAppointment as deleteAppointmentAPI, getAppointmentById } from '../services/appointmentApi';
import { searchEmployees, listActiveEmployees } from '../services/employeeApi';
import { getPhotosByAppointment } from '../services/servicePhotoApi';
import type { ServicePhoto } from '../services/servicePhotoApi';
import { getChecklistResponsesByAppointment } from '../services/serviceChecklistApi';
import type { ServiceChecklistResponse, ChecklistItemResponse } from '../services/serviceChecklistApi';

interface Appointment {
  id: string;
  client_name: string;
  property_name: string;
  service_type: string;
  scheduled_date: string;
  status: string;
  duration?: number; // Tempo estimado em minutos
  actual_duration?: number; // Tempo real gasto em minutos
  end_time?: string; // Hora de término
  client_id?: string; // ID do cliente
  property_id?: string; // ID da propriedade
  employee_id?: string; // ID do funcionário
  employee_name?: string; // Nome do funcionário
}

const Agendamentos: React.FC = () => {
  const { t, formatDate, formatTime } = useI18n();
  const { hasPermission } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();
  const isMobile = useIsMobile();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingAppointment, setRatingAppointment] = useState<Appointment | null>(null);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showCreatePropertyModal, setShowCreatePropertyModal] = useState(false);
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false);
  const [completingAppointmentId, setCompletingAppointmentId] = useState<string | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [clientProperties, setClientProperties] = useState<Property[]>([]);
  const [newAppointment, setNewAppointment] = useState({
    client_name: '',
    property_name: '',
    employee_name: '',
    client_id: '',
    property_id: '',
    employee_id: '', // Necessário para criar appointment
    service_type: 'limpeza',
    scheduled_date: '',
    scheduled_time: '',
    status: 'agendado',
    duration: 120 // Duração padrão em minutos
  });

  // Estado para a folinha (mini calendário)
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [selectedDayISO, setSelectedDayISO] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const formRef = useRef<HTMLFormElement>(null);
  const [photoHistoryAppointment, setPhotoHistoryAppointment] = useState<Appointment | null>(null);
  const [photoHistory, setPhotoHistory] = useState<ServicePhoto[]>([]);
  const [isPhotoHistoryLoading, setIsPhotoHistoryLoading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [checklistAppointment, setChecklistAppointment] = useState<Appointment | null>(null);
  const [checklistResponses, setChecklistResponses] = useState<ServiceChecklistResponse[]>([]);
  const [isChecklistLoading, setIsChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState<string | null>(null);

  // Atalhos de teclado contextuais
  const pageShortcuts: KeyboardShortcut[] = useMemo(() => [
    {
      key: 'c',
      ctrl: true,
      description: 'Criar novo agendamento',
      action: () => {
        if (hasPermission('appointments:create') && !showAppointmentForm) {
          setShowAppointmentForm(true);
          setClientProperties([]);
          setNewAppointment({
            client_name: '',
            property_name: '',
            employee_name: '',
            client_id: '',
            property_id: '',
            employee_id: '',
            service_type: 'limpeza',
            scheduled_date: '',
            scheduled_time: '',
            status: 'agendado',
            duration: 120
          });
        }
      },
      enabled: hasPermission('appointments:create') && !showAppointmentForm,
    },
    {
      key: 's',
      ctrl: true,
      description: 'Salvar agendamento',
      action: () => {
        if (showAppointmentForm && formRef.current) {
          formRef.current.requestSubmit();
        }
      },
      enabled: showAppointmentForm,
    },
    {
      key: 'Escape',
      description: 'Cancelar/Fechar formulário',
      action: () => {
        if (showAppointmentForm) {
          setShowAppointmentForm(false);
          setEditingAppointment(null);
          setNewAppointment({
            client_name: '',
            property_name: '',
            employee_name: '',
            client_id: '',
            property_id: '',
            employee_id: '',
            service_type: 'limpeza',
            scheduled_date: '',
            scheduled_time: '',
            status: 'agendado',
            duration: 120
          });
        }
      },
      enabled: showAppointmentForm,
    },
  ], [hasPermission, showAppointmentForm]);

  useKeyboardShortcuts(pageShortcuts, { ignoreInputs: true });

  const openPhotoHistoryModal = async (appointment: Appointment) => {
    setPhotoHistoryAppointment(appointment);
    setPhotoHistory([]);
    setPhotoError(null);
    setIsPhotoHistoryLoading(true);
    try {
      const data = await getPhotosByAppointment(appointment.id);
      setPhotoHistory(data);
      if (!data.length) {
        setPhotoError('Nenhuma foto encontrada para este agendamento.');
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Erro ao carregar histórico de fotos.';
      setPhotoError(message);
      showError(message);
    } finally {
      setIsPhotoHistoryLoading(false);
    }
  };

  const closePhotoHistoryModal = () => {
    setPhotoHistoryAppointment(null);
    setPhotoHistory([]);
    setPhotoError(null);
  };

  const openChecklistModal = async (appointment: Appointment) => {
    setChecklistAppointment(appointment);
    setChecklistResponses([]);
    setChecklistError(null);
    setIsChecklistLoading(true);
    try {
      const data = await getChecklistResponsesByAppointment(appointment.id);
      setChecklistResponses(data);
      if (!data.length) {
        setChecklistError('Nenhuma ficha encontrada para este atendimento.');
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Erro ao carregar ficha de atendimento.';
      setChecklistError(message);
      showError(message);
    } finally {
      setIsChecklistLoading(false);
    }
  };

  const closeChecklistModal = () => {
    setChecklistAppointment(null);
    setChecklistResponses([]);
    setChecklistError(null);
  };

  const parseChecklistItems = (response: ServiceChecklistResponse): ChecklistItemResponse[] => {
    if (Array.isArray(response.responses)) {
      return response.responses;
    }
    if (!response.responses) return [];
    try {
      const parsed = JSON.parse(response.responses);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Erro ao converter respostas do checklist', error);
      return [];
    }
  };

  const eventDays: MiniCalendarEventDay[] = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of appointments) {
      if (!a.scheduled_date) continue;
      try {
        const d = new Date(a.scheduled_date);
        if (isNaN(d.getTime())) continue;
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        counts[iso] = (counts[iso] || 0) + 1;
      } catch (error) {
        // Erro ao processar data - pular este appointment
        continue;
      }
    }
    return Object.entries(counts).map(([dateISO, count]) => ({ dateISO, count }));
  }, [appointments]);

  const appointmentsOfSelectedDay = useMemo(() => {
    if (!selectedDayISO) return [] as Appointment[];
    return appointments.filter(a => {
      const d = new Date(a.scheduled_date);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return iso === selectedDayISO;
    }).sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime());
  }, [appointments, selectedDayISO]);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return appointments.slice(startIndex, endIndex);
  }, [appointments, currentPage, itemsPerPage]);

  const loadAppointments = async () => {
    setIsLoading(true);
    try {
      const response = await getAppointments();
      setCurrentPage(1); // Reset para primeira página ao recarregar
      
      // Extrair appointments da resposta
      let appointmentsData: any[] = [];
      if (Array.isArray(response)) {
        appointmentsData = response;
      } else if (response && typeof response === 'object') {
        // Tentar diferentes formatos de resposta
        appointmentsData = (response as any).appointments || (response as any).data || (response as any).results || [];
      }
      
      // Transformar os dados da API para o formato esperado pelo frontend
      const mappedAppointments: Appointment[] = appointmentsData.map((apt: any, index: number) => {
        // Converter ID para string se necessário e garantir que seja válido
        let appointmentId = apt.id || apt.ID || apt.appointment_id;
        if (appointmentId && typeof appointmentId !== 'string') {
          appointmentId = String(appointmentId);
        }
        
        if (!appointmentId) {
          // Appointment sem ID - usar ID temporário
        }
        
        // Preservar todos os IDs importantes
        const propertyId = apt.property_id || apt.property_ID || '';
        const employeeId = apt.employee_id || apt.employee_ID || '';
        const clientId = apt.client_id || apt.client_ID || '';
        
        // Extrair scheduled_date - pode estar em diferentes formatos
        let scheduledDate = apt.scheduled_date || apt.ScheduledDate || apt.scheduledDate;
        if (scheduledDate && typeof scheduledDate === 'string') {
          // Se for string, usar diretamente
        } else if (scheduledDate) {
          // Se for objeto Date ou outro formato, converter
          scheduledDate = scheduledDate.toString();
        }
        
        return {
          id: appointmentId || `temp-${index}`,
          // Tentar múltiplas variações do nome do cliente (snake_case e PascalCase)
          client_name: apt.client_name || apt.ClientName || apt.client?.name || 'Cliente não informado',
          // Tentar múltiplas variações do nome da propriedade
          property_name: apt.property_name || apt.PropertyName || apt.property?.name || 'Propriedade não informada',
          service_type: apt.service_type || 'limpeza',
          scheduled_date: scheduledDate || new Date().toISOString(),
          status: apt.status || apt.Status || 'agendado',
          duration: apt.duration || apt.Duration || 120,
          actual_duration: apt.actual_duration || apt.ActualDuration || apt.actualDuration,
          end_time: apt.end_time || apt.EndTime || apt.endTime,
          // Preservar IDs para edição - tentar múltiplas variações
          client_id: clientId || apt.client_id || apt.ClientID || '',
          property_id: propertyId || apt.property_id || apt.PropertyID || '',
          employee_id: employeeId || apt.employee_id || apt.EmployeeID || '',
          employee_name: apt.employee_name || apt.EmployeeName || apt.employee?.name || ''
        };
      });
      
      setAppointments(mappedAppointments);
      setIsLoading(false);
    } catch (error: any) {
      showError('Erro ao carregar agendamentos. Tente recarregar a página.');
      
      // Se for erro 401, pode ser problema de autenticação
      if (error.response?.status === 401) {
        showError('Erro de autenticação. Faça login novamente.');
      }
      
      // Em caso de erro, manter lista vazia em vez de mostrar dados mockados
      setAppointments([]);
      setIsLoading(false);
    }
  };

  const createAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validar campos obrigatórios
      if (!newAppointment.client_id) {
        showWarning('Por favor, selecione um cliente');
        setIsLoading(false);
        return;
      }
      
      if (!newAppointment.property_id) {
        showWarning('Por favor, selecione uma propriedade');
        setIsLoading(false);
        return;
      }
      
      // Garantir que a propriedade selecionada está na lista de propriedades do cliente
      const selectedProperty = clientProperties.find(p => p.id === newAppointment.property_id);
      if (!selectedProperty) {
        showWarning('Por favor, selecione uma propriedade válida');
        setIsLoading(false);
        return;
      }
      
      if (!newAppointment.employee_id) {
        showWarning('Por favor, selecione um funcionário');
        setIsLoading(false);
        return;
      }

      // Combinar data e hora em formato ISO (RFC3339)
      const dateTimeISO = `${newAppointment.scheduled_date}T${newAppointment.scheduled_time}:00Z`;
      
      // Criar appointment usando a API real
      const { createAppointment: createAppointmentAPI } = await import('../services/appointmentApi');
      const createdAppointment = await createAppointmentAPI({
        property_id: newAppointment.property_id,
        employee_id: newAppointment.employee_id,
        scheduled_date: dateTimeISO,
        duration: newAppointment.duration || 120,
        special_instructions: undefined
      });
      
      // Recarregar appointments da API
      await loadAppointments();
      
      setShowAppointmentForm(false);
      setNewAppointment({ 
        client_name: '', 
        property_name: '',
        employee_name: '',
        client_id: '',
        property_id: '',
        employee_id: '',
        service_type: 'limpeza', 
        scheduled_date: '',
        scheduled_time: '',
        status: 'agendado',
        duration: 120
      });
      setIsLoading(false);
      showSuccess(t('appointmentCreatedSuccess') || 'Agendamento criado com sucesso!');
    } catch (error: any) {
      showError(error.response?.data?.error || error.message || 'Erro ao criar agendamento');
      setIsLoading(false);
    }
  };

  const startEdit = async (appointment: Appointment) => {
    setEditingAppointment(appointment);
    
    // Buscar dados completos do appointment da API se não tiver os IDs
    let appointmentData = appointment;
    if (!appointment.property_id || !appointment.employee_id) {
      try {
        const fullAppointment = await getAppointmentById(appointment.id);
        appointmentData = { ...appointment, ...fullAppointment };
      } catch (error) {
        // Erro silencioso - não precisa mostrar toast para este caso
        // Continuar com os dados que temos
      }
    }
    
    // Separar data e hora do scheduled_date
    const appointmentDate = new Date(appointmentData.scheduled_date);
    const dateISO = appointmentDate.toISOString().split('T')[0];
    // Converter hora para formato local (HH:MM)
    const hours = String(appointmentDate.getHours()).padStart(2, '0');
    const minutes = String(appointmentDate.getMinutes()).padStart(2, '0');
    const timeString = `${hours}:${minutes}`;
    
    setNewAppointment({
      client_name: appointmentData.client_name || '',
      property_name: appointmentData.property_name || '',
      employee_name: appointmentData.employee_name || '',
      client_id: appointmentData.client_id || '',
      property_id: appointmentData.property_id || '',
      employee_id: appointmentData.employee_id || '',
      service_type: appointmentData.service_type || 'limpeza',
      scheduled_date: dateISO,
      scheduled_time: timeString,
      status: appointmentData.status || 'agendado',
      duration: appointmentData.duration || 120
    });
    
    // Carregar propriedades do cliente se tiver client_id
    if (appointmentData.client_id) {
      try {
        const properties = await getPropertiesByClient(appointmentData.client_id);
        setClientProperties(properties);
      } catch (error) {
        // Erro silencioso - propriedades serão carregadas quando necessário
      }
    }
    
    setShowAppointmentForm(true);
  };

  const updateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAppointment) return;

    setIsLoading(true);
    try {
      // Validar campos obrigatórios
      if (!newAppointment.property_id) {
        showWarning('Por favor, selecione uma propriedade');
        setIsLoading(false);
        return;
      }
      
      if (!newAppointment.employee_id) {
        showWarning('Por favor, selecione um funcionário');
        setIsLoading(false);
        return;
      }

      // Combinar data e hora em formato ISO
      const dateTimeISO = `${newAppointment.scheduled_date}T${newAppointment.scheduled_time}:00Z`;
      
      // Preparar dados para atualização
      const updateData: any = {
        scheduled_date: dateTimeISO,
        status: newAppointment.status,
        duration: newAppointment.duration || 120
      };

      // Adicionar property_id e employee_id se foram alterados
      if (newAppointment.property_id) {
        updateData.property_id = newAppointment.property_id;
      }
      if (newAppointment.employee_id) {
        updateData.employee_id = newAppointment.employee_id;
      }

      // Chamar API real
      await updateAppointmentAPI(editingAppointment.id, updateData);
      
      // Recarregar appointments da API
      await loadAppointments();
      
      setShowAppointmentForm(false);
      setEditingAppointment(null);
      setNewAppointment({ 
        client_name: '', 
        property_name: '',
        employee_name: '',
        client_id: '',
        property_id: '',
        employee_id: '',
        service_type: 'limpeza', 
        scheduled_date: '',
        scheduled_time: '',
        status: 'agendado',
        duration: 120
      });
      setIsLoading(false);
      showSuccess(t('appointmentUpdatedSuccess') || 'Agendamento atualizado com sucesso!');
    } catch (error: any) {
      showError(error.response?.data?.error || error.message || 'Erro ao atualizar agendamento');
      setIsLoading(false);
    }
  };

  const deleteAppointment = async (appointmentId: string) => {
    if (!confirm('Tem certeza que deseja cancelar este agendamento?')) return;

    setIsLoading(true);
    try {
      // Chamar API real para deletar/cancelar
      await deleteAppointmentAPI(appointmentId);
      
      // Recarregar appointments da API
      await loadAppointments();
      
      setIsLoading(false);
      showSuccess(t('appointmentCancelledSuccess') || 'Agendamento cancelado com sucesso!');
    } catch (error: any) {
      showError(error.response?.data?.error || error.message || 'Erro ao cancelar agendamento');
      setIsLoading(false);
    }
  };

  // Funções de status mantidas para compatibilidade, mas agora usando StatusBadge
  const getStatusColor = (status: string) => {
    // Esta função é mantida apenas para compatibilidade com código existente
    // O novo componente StatusBadge deve ser usado preferencialmente
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    const colorMap: Record<string, string> = {
      agendado: '#1e40af',
      scheduled: '#1e40af',
      confirmado: '#166534',
      confirmed: '#166534',
      em_andamento: '#92400e',
      in_progress: '#92400e',
      concluído: '#065f46',
      completed: '#065f46',
      cancelado: '#991b1b',
      cancelled: '#991b1b',
      atrasado: '#7c2d12',
      overdue: '#7c2d12',
    };
    return colorMap[normalizedStatus] || '#6b7280';
  };

  const getServiceTypeLabel = (type: string) => {
    switch (type) {
      case 'limpeza': return t('cleaning');
      case 'manutenção': return t('maintenance');
      case 'reparo': return t('repair');
      default: return type;
    }
  };

  const getStatusLabel = (status: string) => {
    // Esta função é mantida apenas para compatibilidade
    // O novo componente StatusBadge deve ser usado preferencialmente
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    const labelMap: Record<string, string> = {
      agendado: t('scheduled'),
      scheduled: t('scheduled'),
      confirmado: t('confirmed'),
      confirmed: t('confirmed'),
      em_andamento: t('inProgress'),
      in_progress: t('inProgress'),
      concluído: t('completed'),
      completed: t('completed'),
      cancelado: t('cancelled'),
      cancelled: t('cancelled'),
      atrasado: t('overdue'),
      overdue: t('overdue'),
    };
    return labelMap[normalizedStatus] || status;
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  return (
    <div className="responsive-container" style={{ padding: isMobile ? '16px' : '30px' }}>
      <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333', fontSize: isMobile ? '24px' : '32px' }}>{t('appointmentManagement')}</h1>
        <p style={{ color: '#666', margin: 0, fontSize: isMobile ? '14px' : '16px' }}>{t('appointmentSubtitle')}</p>
      </div>

      {/* Folinha de calendário estilo Google */}
      <div style={{ marginBottom: 20 }}>
        <MiniMonthCalendar
          currentMonth={calendarMonth}
          onMonthChange={setCalendarMonth}
          selectedDate={selectedDayISO}
          onSelectDate={setSelectedDayISO}
          eventDays={eventDays}
        />
      </div>

      {/* Detalhamento do dia selecionado */}
      {selectedDayISO && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid #eef2f7'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, color: '#333' }}>Agendamentos de {selectedDayISO.split('-').reverse().join('/')}</h3>
            <button
              onClick={() => setSelectedDayISO(undefined)}
              style={{ border: 'none', background: 'transparent', color: '#9333ea', cursor: 'pointer' }}
            >
              Limpar seleção
            </button>
          </div>
          {appointmentsOfSelectedDay.length === 0 ? (
            <div style={{ color: '#6b7280' }}>{t('reports.noAppointmentsDay')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {appointmentsOfSelectedDay.map((appointment) => (
                <div key={`sel-${appointment.id}`} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  background: '#f9fafb',
                  borderRadius: 6,
                  border: '1px solid #eef2f7'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Calendar style={{ color: '#9333ea' }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#374151' }}>{getServiceTypeLabel(appointment.service_type)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {(() => {
                          if (!appointment.scheduled_date) {
                            return 'Data não informada';
                          }
                          try {
                            const date = new Date(appointment.scheduled_date);
                            if (isNaN(date.getTime())) {
                              return 'Data inválida';
                            }
                            return `${formatDate(date)} ${formatTime(date)}`;
                          } catch (error) {
                            // Erro ao formatar data - usar fallback
                            return 'Data inválida';
                          }
                        })()}
                        {' • '}{appointment.client_name} • {appointment.property_name}
                      </div>
                    </div>
                  </div>
                  <StatusBadge 
                    status={appointment.status} 
                    size="small"
                    showIcon={true}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('appointmentList')}</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            {hasPermission('appointments:create') && (
              <button
                onClick={() => {
                  setShowAppointmentForm(true);
                  // Resetar estados ao abrir formulário
                  setClientProperties([]);
                  setNewAppointment({
                    client_name: '',
                    property_name: '',
                    employee_name: '',
                    client_id: '',
                    property_id: '',
                    employee_id: '',
                    service_type: 'limpeza',
                    scheduled_date: '',
                    scheduled_time: '',
                    status: 'agendado',
                    duration: 120
                  });
                }}
                style={{
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Plus size={16} />
                {t('newAppointment')}
              </button>
            )}
            <button
              onClick={() => setShowRecurrenceModal(true)}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Repeat size={16} />{t('appointments.recurring')}</button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('loadingAppointments')}</div>
          </div>
        ) : appointments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Calendar style={{ fontSize: '48px', marginBottom: '10px', color: '#9333ea' }} />
            <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
              {t('noAppointmentsFound')}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Não há agendamentos cadastrados no sistema ainda.
            </div>
            {hasPermission('appointments:create') && (
              <button
                onClick={() => {
                  setShowAppointmentForm(true);
                  setClientProperties([]);
                  setNewAppointment({
                    client_name: '',
                    property_name: '',
                    employee_name: '',
                    client_id: '',
                    property_id: '',
                    employee_id: '',
                    service_type: 'limpeza',
                    scheduled_date: '',
                    scheduled_time: '',
                    status: 'agendado',
                    duration: 120
                  });
                }}
                style={{
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Plus size={16} />{t('appointments.createFirst')}</button>
            )}
            <div style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
              Total: {appointments.length} appointments
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {paginatedAppointments.map((appointment) => (
              <div
                key={appointment.id}
                style={{
                  border: '1px solid #eee',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Calendar style={{ marginRight: '8px', color: '#9333ea' }} />
                    <div>
                      <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                        {getServiceTypeLabel(appointment.service_type)}
                      </h4>
                      <div style={{ display: 'flex', alignItems: 'center', color: '#666', fontSize: '14px' }}>
                        <Clock style={{ marginRight: '5px' }} />
                        {(() => {
                          if (!appointment.scheduled_date) {
                            return 'Data não informada';
                          }
                          try {
                            const date = new Date(appointment.scheduled_date);
                            if (isNaN(date.getTime())) {
                              return 'Data inválida';
                            }
                            return `${formatDate(date)} ${formatTime(date)}`;
                          } catch (error) {
                            // Erro ao formatar data - usar fallback
                            return 'Data inválida';
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                  <StatusBadge 
                    status={appointment.status} 
                    size="medium"
                    showIcon={true}
                  />
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: '#666' }}>
                  <User style={{ marginRight: '8px', fontSize: '14px' }} />
                  <span style={{ fontSize: '14px' }}>{appointment.client_name}</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
                  <MapPin style={{ marginRight: '8px', fontSize: '14px' }} />
                  <span style={{ fontSize: '14px' }}>{appointment.property_name}</span>
                </div>

                {/* Informações de Tempo */}
                {appointment.status === 'completed' && (appointment.actual_duration || appointment.duration) && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '10px', 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: '6px',
                    borderLeft: '3px solid #3b82f6'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
                        <Clock style={{ marginRight: '6px', fontSize: '14px' }} />
                        <span><strong>Tempo Estimado:</strong> {appointment.duration || 0} min</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', color: appointment.actual_duration && appointment.duration 
                        ? (appointment.actual_duration > appointment.duration ? '#dc2626' : '#10b981')
                        : '#666'
                      }}>
                        <Clock style={{ marginRight: '6px', fontSize: '14px' }} />
                        <span><strong>Tempo Real:</strong> {appointment.actual_duration || appointment.duration || 0} min</span>
                      </div>
                    </div>
                    {appointment.actual_duration && appointment.duration && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: appointment.actual_duration > appointment.duration ? '#dc2626' : '#10b981',
                        fontWeight: '500'
                      }}>
                        {appointment.actual_duration > appointment.duration 
                          ? `⚠️ Excedeu por ${appointment.actual_duration - appointment.duration} minutos`
                          : appointment.actual_duration < appointment.duration
                          ? `✅ Economizou ${appointment.duration - appointment.actual_duration} minutos`
                          : '✅ Tempo exato'}
                      </div>
                    )}
                  </div>
                )}

                {/* Botões de Ação */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => openPhotoHistoryModal(appointment)}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#9333ea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <Camera size={14} />
                    Fotos
                  </button>
                  <button
                    onClick={() => openChecklistModal(appointment)}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <ClipboardList size={14} />
                    Ficha Atendimento
                  </button>
                  {appointment.status !== 'concluído' && hasPermission('appointments:update') && (
                    <button
                      onClick={() => {
                        setCompletingAppointmentId(appointment.id);
                        setShowCompleteModal(true);
                      }}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <CheckCircle size={14} />
                      Finalizar
                    </button>
                  )}
                  {hasPermission('appointments:update') && (
                    <button
                      onClick={() => startEdit(appointment)}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <Edit size={14} />{t('actions.edit')}</button>
                  )}
                  {hasPermission('appointments:cancel') && (
                    <button
                      onClick={() => deleteAppointment(appointment.id)}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <Trash2 size={14} />{t('actions.cancel')}</button>
                  )}
                </div>
              </div>
              ))}
            </div>
            {appointments.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(appointments.length / itemsPerPage)}
                totalItems={appointments.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onItemsPerPageChange={(newItemsPerPage) => {
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Modal de Agendamento */}
      {showAppointmentForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              {editingAppointment ? 'Editar Agendamento' : t('newAppointment')}
            </h3>
            <form ref={formRef} onSubmit={editingAppointment ? updateAppointment : createAppointment} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <Autocomplete
                label={t('client')}
                value={newAppointment.client_name}
                onChange={async (value, option) => {
                  setNewAppointment({
                    ...newAppointment,
                    client_name: value,
                    client_id: option?.id || '',
                    property_name: '', // Limpar propriedade quando mudar cliente
                    property_id: ''
                  });
                  
                  // Se selecionou um cliente, buscar suas propriedades
                  if (option?.id) {
                    try {
                      const properties = await getPropertiesByClient(option.id);
                      setClientProperties(properties);
                    } catch (err) {
                      showError(t('appointments.error.loadingProperties'));
                      setClientProperties([]);
                    }
                  } else {
                    setClientProperties([]);
                  }
                }}
                onSearch={async (query) => {
                  const clients = await searchClients(query);
                  return clients.map(c => ({
                    id: c.id,
                    label: c.name,
                    address: c.phone ? `Tel: ${c.phone}` : undefined
                  }));
                }}
                placeholder={t('appointments.searchClientMin')}
                required
                minChars={3}
                showCreateOption={true}
                onCreateNew={() => {
                  setShowCreateClientModal(true);
                }}
              />
              
              {newAppointment.client_id && clientProperties.length > 0 ? (
                // Mostrar Autocomplete de propriedades do cliente selecionado
                <Autocomplete
                  label={t('property')}
                  value={newAppointment.property_name}
                  onChange={(value, option) => {
                    setNewAppointment({
                      ...newAppointment,
                      property_id: option?.id || '',
                      property_name: value
                    });
                  }}
                  onSearch={async () => {
                    // Retornar apenas propriedades do cliente selecionado, sem necessidade de busca
                    return clientProperties
                      .filter(prop => prop.client_id === newAppointment.client_id)
                      .map(prop => ({
                        id: prop.id,
                        label: prop.name,
                        address: prop.address || undefined
                      }));
                  }}
                  placeholder="Selecione uma propriedade..."
                  required
                  minChars={0}
                  loadOnFocus={true}
                  showCreateOption={true}
                  onCreateNew={() => {
                    setShowCreatePropertyModal(true);
                  }}
                />
              ) : (
                // Mostrar autocomplete de propriedades quando não há cliente selecionado
                <Autocomplete
                  label={t('property')}
                  value={newAppointment.property_name}
                  onChange={(value, option) => {
                    setNewAppointment({
                      ...newAppointment,
                      property_name: value,
                      property_id: option?.id || ''
                    });
                  }}
                  onSearch={async (query) => {
                    const properties = await searchProperties(query);
                    return properties.map(p => ({
                      id: p.id,
                      label: p.name,
                      address: p.address || undefined
                    }));
                  }}
                  placeholder={newAppointment.client_id ? "Digite para buscar ou selecione do dropdown acima" : "Selecione um cliente primeiro ou digite para buscar..."}
                  required
                  minChars={3}
                  showCreateOption={!!newAppointment.client_id}
                  onCreateNew={() => {
                    if (!newAppointment.client_id) {
                      showWarning('Selecione um cliente primeiro');
                      return;
                    }
                    setShowCreatePropertyModal(true);
                  }}
                />
              )}
              <Autocomplete
                label={t('common.employee')}
                value={newAppointment.employee_name}
                onChange={(value, option) => {
                  // Só atualizar se uma opção válida foi selecionada
                  if (option?.id) {
                    setNewAppointment({
                      ...newAppointment,
                      employee_name: value,
                      employee_id: option.id
                    });
                  } else {
                    // Se não há opção selecionada, limpar o employee_id
                    setNewAppointment({
                      ...newAppointment,
                      employee_name: value,
                      employee_id: ''
                    });
                  }
                }}
                onSearch={async (query: string) => {
                  let employees;
                  // Se query estiver vazia, buscar todos os funcionários ativos
                  if (query.trim() === '') {
                    employees = await listActiveEmployees();
                  } else {
                    employees = await searchEmployees(query);
                  }
                  return employees.map(emp => ({
                    id: emp.id,
                    label: emp.name || emp.email || 'Sem nome',
                    address: emp.zone ? `Zona: ${emp.zone}` : emp.email || undefined
                  }));
                }}
                placeholder="Digite para buscar ou clique para ver todos..."
                required
                minChars={0}
                loadOnFocus={true}
                showCreateOption={true}
                onCreateNew={() => {
                  setShowCreateEmployeeModal(true);
                }}
              />
              {newAppointment.employee_name && !newAppointment.employee_id && (
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>⚠️</span>
                  <span>Por favor, selecione um funcionário da lista acima</span>
                </div>
              )}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('serviceType')}</label>
                <select
                  value={newAppointment.service_type}
                  onChange={(e) => setNewAppointment({...newAppointment, service_type: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none'
                  }}
                >
                  <option value="limpeza">{t('cleaning')}</option>
                  <option value="manutenção">{t('maintenance')}</option>
                  <option value="reparo">{t('repair')}</option>
                </select>
              </div>
              <div>
                <DateInput
                  label={t('dateTime') || 'Data'}
                  value={newAppointment.scheduled_date}
                  onChange={(value) => setNewAppointment({...newAppointment, scheduled_date: value})}
                  placeholder="DD/MM/YYYY"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('dateTime') || 'Hora'}</label>
                <input
                  type="time"
                  value={newAppointment.scheduled_time}
                  onChange={(e) => setNewAppointment({...newAppointment, scheduled_time: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none'
                  }}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: editingAppointment ? '#10b981' : '#9333ea',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {isLoading ? t('saving') : (editingAppointment ? 'Atualizar' : t('schedule'))}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAppointmentForm(false);
                    setEditingAppointment(null);
                    setClientProperties([]); // Limpar propriedades ao fechar
                    setNewAppointment({ 
                      client_name: '', 
                      property_name: '',
                      employee_name: '',
                      client_id: '',
                      property_id: '',
                      employee_id: '',
                      service_type: 'limpeza', 
                      scheduled_date: '',
                      scheduled_time: '',
                      status: 'agendado',
                      duration: 120
                    });
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Histórico de Fotos */}
      {photoHistoryAppointment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: 'clamp(320px, 95vw, 1000px)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#111827' }}>Histórico de Fotos</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                  {photoHistoryAppointment.client_name} — {photoHistoryAppointment.property_name}
                </p>
              </div>
              <button
                onClick={closePhotoHistoryModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Fechar ✕
              </button>
            </div>

            {isPhotoHistoryLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Loader2 className="spin" size={28} color="#6b7280" />
              </div>
            ) : (
              <>
                {photoError && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      color: '#991b1b',
                      marginBottom: '16px',
                      fontSize: '14px',
                    }}
                  >
                    {photoError}
                  </div>
                )}

                {!photoError && (
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {(['before', 'after'] as const).map((category) => {
                      const photosByCategory = photoHistory.filter((photo) => photo.category === category);
                      return (
                        <div key={category}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <Image size={18} color={category === 'before' ? '#ec4899' : '#10b981'} />
                            <h4 style={{ margin: 0, color: '#111827' }}>
                              {category === 'before' ? 'Fotos Antes' : 'Fotos Depois'}
                              <span style={{ marginLeft: '6px', color: '#6b7280', fontSize: '13px' }}>
                                ({photosByCategory.length})
                              </span>
                            </h4>
                          </div>
                          {photosByCategory.length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                              Nenhuma foto {category === 'before' ? 'antes' : 'depois'} registrada.
                            </p>
                          ) : (
                            <div
                              style={{
                                display: 'grid',
                                gap: '12px',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                              }}
                            >
                              {photosByCategory.map((photo) => {
                                const imageUrl =
                                  photo.optimized_url ||
                                  photo.thumbnail_url ||
                                  photo.original_url ||
                                  photo.url ||
                                  '';
                                return (
                                  <div
                                    key={photo.id}
                                    style={{
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      backgroundColor: '#f9fafb',
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={photo.description || 'Foto de serviço'}
                                        style={{ width: '100%', height: '130px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          height: '130px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#e5e7eb',
                                          color: '#6b7280',
                                          fontSize: '12px',
                                        }}
                                      >
                                        Sem prévia
                                      </div>
                                    )}
                                    <div style={{ padding: '10px', fontSize: '12px', color: '#4b5563' }}>
                                      {photo.room_name && (
                                        <p style={{ margin: '0 0 4px 0', fontWeight: 600 }}>{photo.room_name}</p>
                                      )}
                                      {photo.description && (
                                        <p style={{ margin: '0 0 6px 0' }}>{photo.description}</p>
                                      )}
                                      {photo.created_at && (
                                        <p style={{ margin: 0, color: '#9ca3af' }}>
                                          {new Date(photo.created_at).toLocaleString()}
                                        </p>
                                      )}
                                      {imageUrl && (
                                        <a
                                          href={imageUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ display: 'inline-block', marginTop: '6px', color: '#2563eb' }}
                                        >
                                          Abrir em nova aba
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Ficha de Atendimento */}
      {checklistAppointment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: 'clamp(320px, 95vw, 900px)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              padding: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#111827' }}>Ficha de Atendimento</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                  {checklistAppointment.client_name} — {checklistAppointment.property_name}
                </p>
              </div>
              <button
                onClick={closeChecklistModal}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Fechar ✕
              </button>
            </div>

            {isChecklistLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Loader2 className="spin" size={28} color="#6b7280" />
              </div>
            ) : (
              <>
                {checklistError && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      color: '#991b1b',
                      marginBottom: '16px',
                      fontSize: '14px',
                    }}
                  >
                    {checklistError}
                  </div>
                )}

                {!checklistError && (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {(['pre_service', 'post_service'] as const).map((phase) => {
                      const responsesByType = checklistResponses.filter((response) => response.type === phase);
                      return (
                        <div key={phase}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <ClipboardList size={18} color={phase === 'pre_service' ? '#2563eb' : '#16a34a'} />
                            <h4 style={{ margin: 0, color: '#111827' }}>
                              {phase === 'pre_service' ? 'Checklist Pré-Serviço' : 'Checklist Pós-Serviço'}
                              <span style={{ marginLeft: '6px', color: '#6b7280', fontSize: '13px' }}>
                                ({responsesByType.length})
                              </span>
                            </h4>
                          </div>
                          {responsesByType.length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                              Nenhum registro {phase === 'pre_service' ? 'antes' : 'depois'} disponível.
                            </p>
                          ) : (
                            responsesByType.map((response) => {
                              const items = parseChecklistItems(response);
                              return (
                                <div
                                  key={response.id}
                                  style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '12px',
                                    backgroundColor: '#f9fafb',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                    <div>
                                      <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>
                                        {response.template_name || 'Checklist personalizado'}
                                      </p>
                                      {response.employee_name && (
                                        <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '12px' }}>
                                          Técnico: {response.employee_name}
                                        </p>
                                      )}
                                    </div>
                                    {response.created_at && (
                                      <p style={{ margin: 0, color: '#9ca3af', fontSize: '12px' }}>
                                        {new Date(response.created_at).toLocaleString()}
                                      </p>
                                    )}
                                  </div>

                                  <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                                    {items.map((item, index) => (
                                      <div
                                        key={`${response.id}-${index}`}
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          backgroundColor: '#fff',
                                          borderRadius: '6px',
                                          padding: '10px 12px',
                                          border: '1px solid #e5e7eb',
                                        }}
                                      >
                                        <div>
                                          <p style={{ margin: 0, fontWeight: 500 }}>{item.item}</p>
                                          {item.notes && (
                                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>{item.notes}</p>
                                          )}
                                        </div>
                                        <span
                                          style={{
                                            padding: '2px 8px',
                                            borderRadius: '999px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: item.completed ? '#15803d' : '#b45309',
                                            backgroundColor: item.completed ? '#dcfce7' : '#fef3c7',
                                          }}
                                        >
                                          {item.completed ? 'Concluído' : 'Pendente'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>

                                  {response.notes && (
                                    <div
                                      style={{
                                        marginTop: '12px',
                                        padding: '10px 12px',
                                        backgroundColor: '#fff7ed',
                                        borderRadius: '6px',
                                        border: '1px solid #fed7aa',
                                        fontSize: '13px',
                                        color: '#92400e',
                                      }}
                                    >
                                      <strong>Notas: </strong>
                                      {response.notes}
                                    </div>
                                  )}

                                  {response.client_signature && (
                                    <div style={{ marginTop: '12px' }}>
                                      <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#6b7280' }}>{t('appointments.clientSignature')}</p>
                                      <img
                                        src={response.client_signature}
                                        alt="Assinatura do cliente"
                                        style={{ maxWidth: '260px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Recorrência */}
      <RecurrenceModal
        isOpen={showRecurrenceModal}
        onClose={() => setShowRecurrenceModal(false)}
        onSuccess={() => {
          loadAppointments();
          setShowRecurrenceModal(false);
        }}
      />

      {/* Modal de Finalizar Serviço */}
      {completingAppointmentId && (
        <CompleteServiceModal
          isOpen={showCompleteModal}
          onClose={() => {
            setShowCompleteModal(false);
            setCompletingAppointmentId(null);
          }}
          appointmentId={completingAppointmentId}
          estimatedDuration={appointments.find(a => a.id === completingAppointmentId)?.duration || 120}
          onSuccess={() => {
            loadAppointments();
            setShowCompleteModal(false);
            // Mostrar modal de avaliação após completar serviço
            const completedAppointment = appointments.find(a => a.id === completingAppointmentId);
            if (completedAppointment && completedAppointment.employee_id) {
              setRatingAppointment(completedAppointment);
              setShowRatingModal(true);
            }
            setCompletingAppointmentId(null);
          }}
        />
      )}

      {/* Modal de Avaliação */}
      {ratingAppointment && showRatingModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3001,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: 'clamp(300px, 90%, 600px)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            }}
          >
            <RatingForm
              appointmentId={ratingAppointment.id}
              employeeId={ratingAppointment.employee_id || ''}
              employeeName={ratingAppointment.employee_name}
              propertyName={ratingAppointment.property_name}
              onSuccess={() => {
                setShowRatingModal(false);
                setRatingAppointment(null);
                loadAppointments();
              }}
              onCancel={() => {
                setShowRatingModal(false);
                setRatingAppointment(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Modal de Criar Cliente Rápido */}
      <QuickCreateClientModal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        initialName={newAppointment.client_name}
        onSuccess={async (client) => {
          setNewAppointment({
            ...newAppointment,
            client_name: client.name,
            client_id: client.id
          });
          setShowCreateClientModal(false);
          
          // Buscar propriedades do novo cliente
          try {
            const properties = await getPropertiesByClient(client.id);
            setClientProperties(properties);
          } catch (err) {
            showError(t('appointments.error.loadingProperties'));
            setClientProperties([]);
          }
        }}
      />

      {/* Modal de Criar Propriedade Rápida */}
      <QuickCreatePropertyModal
        isOpen={showCreatePropertyModal}
        onClose={() => setShowCreatePropertyModal(false)}
        clientId={newAppointment.client_id}
        clientName={newAppointment.client_name}
        initialName={newAppointment.property_name}
        onSuccess={async (property) => {
          setNewAppointment({
            ...newAppointment,
            property_name: property.name,
            property_id: property.id
          });
          setShowCreatePropertyModal(false);
          
          // Atualizar lista de propriedades
          try {
            const properties = await getPropertiesByClient(newAppointment.client_id);
            setClientProperties(properties);
          } catch (err) {
            showError('Erro ao atualizar propriedades');
          }
        }}
      />

      {/* Modal de Criar Funcionário Rápido */}
      <QuickCreateEmployeeModal
        isOpen={showCreateEmployeeModal}
        onClose={() => setShowCreateEmployeeModal(false)}
        initialName={newAppointment.employee_name}
        onSuccess={(employee) => {
          setNewAppointment({
            ...newAppointment,
            employee_name: employee.name || employee.email || '',
            employee_id: employee.id
          });
          setShowCreateEmployeeModal(false);
        }}
      />
    </div>
  );
};

export default Agendamentos;

